import { ChangeDetectionStrategy, Component, computed, input, model, output } from '@angular/core';
import { Search } from 'lucide';
import { NbIcon } from '../../icon/icon';
import { injectNbUiText } from '../../i18n/ui-text';

/**
 * `<nb-topbar>` — the desktop top bar. Two search modes:
 *
 * - default: a live `<input type="search">` two-way bound to `searchValue`;
 * - `[command]="true"` (2026-09 styre prototype): the search is a BUTTON that
 *   opens the command palette (`(commandOpen)`), with a visible `⌘K` hint.
 *   Pair it with `<nb-command-palette>`, which also listens for ⌘K / Ctrl+K.
 *
 * Project actions (icon buttons, a primary button) after the search.
 */
@Component({
  selector: 'nb-topbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  template: `<div class="topbar">
    @if (command()) {
      <button type="button" class="search cmd" (click)="commandOpen.emit()" [attr.aria-label]="searchAriaLabel() || placeholderText()">
        <nb-icon [icon]="Search" />
        <span class="ph">{{ placeholderText() }}</span>
        <kbd aria-hidden="true">{{ isMac ? '⌘' : 'Ctrl' }} K</kbd>
      </button>
    } @else {
      <div class="search">
        <nb-icon [icon]="Search" />
        <input
          type="search"
          [placeholder]="placeholderText()"
          [attr.aria-label]="searchAriaLabel() || placeholderText()"
          [value]="searchValue()"
          (input)="searchValue.set($any($event.target).value)"
        />
      </div>
    }
    <ng-content />
  </div>`,
  styleUrl: './topbar.css',
})
export class NbTopbar {
  /** Placeholder + fallback accessible name for the search field. */
  readonly searchPlaceholder = input<string | undefined>(undefined);
  private readonly uiText = injectNbUiText();
  /** The placeholder actually rendered — the input when given, else the app's design-system copy. */
  protected readonly placeholderText = computed(() => this.searchPlaceholder() ?? this.uiText().searchPlaceholder);
  /** Two-way bound value of the search field (input mode only). */
  readonly searchValue = model('');
  /** Explicit accessible name for the search field; falls back to the placeholder. */
  readonly searchAriaLabel = input<string>('');
  /** Render the search as a command-palette trigger instead of a live input. */
  readonly command = input(false);
  /** Emits when the command trigger is pressed. */
  readonly commandOpen = output<void>();

  protected readonly Search = Search;
  protected readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
}
